/**
 * SSE Service for Real-time WhatsApp Messages
 * Handles Server-Sent Events connection and message broadcasting
 */

class SSEService {
  constructor() {
    this.eventSource = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.listeners = new Map();
    this.heartbeatInterval = null;
    this.lastHeartbeat = null;
  }

  /**
   * Connect to SSE endpoint
   * @param {string} accessToken - User access token
   * @param {Object} options - Connection options
   */
  connect(accessToken, options = {}) {
    if (this.eventSource) {
      this.disconnect();
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:1337';
    const token = accessToken?.token || accessToken;
    const url = `${baseUrl}/v2/whatsapp/internal/sse-messages?token=${encodeURIComponent(token)}`;

    try {
      this.eventSource = new EventSource(url);

      this.setupEventListeners(options);
      this.startHeartbeatCheck();

    } catch (error) {
      console.error('SSE: Failed to create EventSource:', error);
      this.handleReconnect(accessToken, options);
    }
  }

  /**
   * Set up event listeners for SSE
   * @param {Object} options - Connection options
   */
  setupEventListeners(options) {
    if (!this.eventSource) return;

    // Connection opened
    this.eventSource.onopen = (event) => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.emit('connected', { event });

      if (options.onConnected) {
        options.onConnected(event);
      }
    };

    // Connection error
    this.eventSource.onerror = (event) => {
      console.error('SSE: Connection error', event);
      this.isConnected = false;
      this.emit('error', { event });

      if (options.onError) {
        options.onError(event);
      }

      // Attempt to reconnect
      this.handleReconnect(options.accessToken, options);
    };

    // Message received
    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'heartbeat') {
          this.lastHeartbeat = new Date();
          return; // Don't emit heartbeat events
        }

        this.emit('message', data);

        if (options.onMessage) {
          options.onMessage(data);
        }
      } catch (error) {
        console.error('SSE: Failed to parse message:', event.data, error);
      }
    };

    // Specific event listeners
    this.eventSource.addEventListener('message', (event) => {
      this.handleMessageEvent(event, options);
    });

    this.eventSource.addEventListener('status_update', (event) => {
      this.handleStatusUpdateEvent(event, options);
    });

    this.eventSource.addEventListener('conversation_assignment', (event) => {
      this.handleAssignmentEvent(event, options);
    });
  }

  /**
   * Handle incoming message events
   * @param {MessageEvent} event - SSE message event
   * @param {Object} options - Connection options
   */
  handleMessageEvent(event, options) {
    try {
      const messageData = JSON.parse(event.data);

      if (options.onNewMessage) {
        options.onNewMessage(messageData);
      }
    } catch (error) {
      console.error('SSE: Failed to handle message event:', error);
    }
  }

  /**
   * Handle message status update events
   * @param {MessageEvent} event - SSE status update event
   * @param {Object} options - Connection options
   */
  handleStatusUpdateEvent(event, options) {
    try {
      const statusData = JSON.parse(event.data);

      if (options.onStatusUpdate) {
        options.onStatusUpdate(statusData);
      }
    } catch (error) {
      console.error('SSE: Failed to handle status update event:', error);
    }
  }

  /**
   * Handle conversation assignment events
   * @param {MessageEvent} event - SSE assignment event
   * @param {Object} options - Connection options
   */
  handleAssignmentEvent(event, options) {
    try {
      const assignmentData = JSON.parse(event.data);

      if (options.onAssignmentUpdate) {
        options.onAssignmentUpdate(assignmentData);
      }
    } catch (error) {
      console.error('SSE: Failed to handle assignment event:', error);
    }
  }

  /**
   * Handle reconnection logic
   * @param {string} accessToken - User access token
   * @param {Object} options - Connection options
   */
  handleReconnect(accessToken, options) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('maxReconnectAttemptsReached');
      return;
    }

    this.reconnectAttempts++;

    setTimeout(() => {
      this.connect(accessToken, options);
    }, this.reconnectDelay);

    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
  }

  /**
   * Start heartbeat monitoring
   */
  startHeartbeatCheck() {
    this.heartbeatInterval = setInterval(() => {
      if (this.lastHeartbeat) {
        const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat.getTime();
        const maxHeartbeatInterval = 45000; // 45 seconds (30s heartbeat + buffer)

        if (timeSinceLastHeartbeat > maxHeartbeatInterval) {
          this.isConnected = false;
          this.reconnectAttempts = 0; // Reset attempts for heartbeat timeout
          // The onerror handler will trigger reconnection
        }
      }
    }, 10000); // Check every 10 seconds
  }

  /**
   * Disconnect from SSE
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    this.isConnected = false;
    this.emit('disconnected');
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('SSE: Error in event listener:', error);
        }
      });
    }
  }

  /**
   * Get connection status
   * @returns {Object} Connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      lastHeartbeat: this.lastHeartbeat
    };
  }
}

// Create singleton instance
const sseService = new SSEService();

export default sseService;
